/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Injectable } from '@angular/core';
import {
  BuilderFormSchemaFactory,
  FormControlSchemaConfig,
  FormRootSchema,
  FormState,
  FormStateSchema,
} from '@gh/builder-shared';
import { CustomFieldType } from '@gh/builder-shared-data';
import { BaseNumberType, DateTimeTypes, getRangedDecimalParams } from '@gh/common-shared';
import { numberType, NumberType, Type, TypeService } from '@gh/core-type';
import { ContactCustomFieldItem, ContactCustomFieldList } from '../../resources/model/contact-shared.model';

@Injectable()
export class ContactDetailsSharedCustomFieldsFormStateFactory {
  constructor(private schemaFactory: BuilderFormSchemaFactory,
              private typeService: TypeService) {

  }

  create(): ContactDetailsSharedCustomFieldsFormState {
    return new ContactDetailsSharedCustomFieldsFormState(this.schemaFactory, this.typeService);
  }
}

export class ContactDetailsSharedCustomFieldsFormState extends FormState<ContactCustomFieldList> {
  numberTypes: Hash<NumberType> = {};

  constructor(private schemaFactory: BuilderFormSchemaFactory,
              private typeService: TypeService) {
    super(schemaFactory);
  }

  protected buildSchema(customFieldList: ContactCustomFieldList,
                        { root, array, group }: FormStateSchema): FormRootSchema {
    return root({
      children: {
        data: array({
          children: customFieldList.data.map((item) => group({
            key: item.customFieldId,
            children: {
              customFieldId: void 0,
              name: void 0,
              dataType: void 0,
              customFieldPlacement: void 0,
              required: void 0,
              customFieldValueId: void 0,
              textValue: this.getCustomFieldControlConfig(item, CustomFieldType.Text),
              dateValue: this.getCustomFieldControlConfig(item, CustomFieldType.Date),
              numberValue: this.getCustomFieldControlConfig(item, CustomFieldType.Number),
              digitsLength: void 0,
              decimalPlaces: void 0,
            },
          })),
        }),
      },
    });
  }

  private getCustomFieldControlConfig(item: ContactCustomFieldItem,
                                      dataType: CustomFieldType): FormControlSchemaConfig {
    let type: Maybe<Type<any>>;
    if (item.dataType === CustomFieldType.Number) {
      type = this.createNumberType(item);
    } else if (item.dataType === CustomFieldType.Date) {
      type = DateTimeTypes.ShortDate;
    }
    return {
      required: item.dataType === dataType && item.required,
      type,
    };
  }

  private createNumberType(item: ContactCustomFieldItem): NumberType {
    const type = this.typeService.create(numberType(BaseNumberType, {
      ...getRangedDecimalParams({ precision: item.digitsLength || 0, scale: item.decimalPlaces || 0 }),
      messages: {
        range: 'msg_field_invalid_range',
      },
    }));
    this.numberTypes[item.customFieldId] = type;
    return type;
  }
}
